import { Button, Column, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { CenteredSpinner } from '~/commonComponents';

/**
 * Full-render loading state shared by the BID screens/tabs. Wraps the common
 * `CenteredSpinner` in a flex column so the indicator sits centered with a
 * comfortable vertical footprint, and forwards `data-cy` to the container so
 * callers keep their existing test hooks.
 */
export const BidLoadingState = ({ 'data-cy': dataCy }: { 'data-cy'?: string }) => (
  <Column flex width="100%" data-cy={dataCy}>
    <CenteredSpinner minHeight={128} />
  </Column>
);

/**
 * Full-render error-with-retry state shared by the BID query screens (history
 * page, application form). Renders the message and a "Try again" button wired
 * to the caller's `refetch`.
 */
export const BidErrorState = ({
  message,
  onRetry,
  'data-cy': dataCy,
}: {
  message: string;
  onRetry: () => void;
  'data-cy'?: string;
}) => (
  <Column align="center" gap={4} py={16} data-cy={dataCy}>
    <Typography>{message}</Typography>
    <Button onClick={onRetry}>Try again</Button>
  </Column>
);

/**
 * Inline submit-error state shared by the BID mutation forms (application,
 * EOD/pending feedback). Renders the error message and, when `onRetry` is
 * given, an outlined "Try again" button wired to the caller's
 * `mutation.reset()`. A form that leaves Submit live after the server refused
 * the values omits `onRetry`: retrying the same values would fail the same way.
 * Callers gate it on `mutation.isError`.
 */
export const BidSubmitError = ({
  message,
  onRetry,
  'data-cy': dataCy,
}: {
  message: string;
  onRetry?: () => void;
  'data-cy'?: string;
}) => (
  <Column gap={2} data-cy={dataCy}>
    <Typography color={colors.error[500]}>{message}</Typography>
    {onRetry && (
      <Button type="button" variant="outlined" onClick={onRetry}>
        Try again
      </Button>
    )}
  </Column>
);
